import { useEffect, useState, useRef } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import adminStore from "./admin/src/redux/store";
import AdminDashboard from "./admin-panel/pages/Dashboard";
import AdminLayout from "./admin-panel/layout/AdminLayout";
import KYC from "./admin-panel/pages/Kyc";
import KycVerification from "./admin-panel/pages/KycVerification";
import ProfileSetup from "./admin-panel/pages/ProfileSetup";
import DigiLockerCallback from "./admin-panel/pages/DigiLockerCallback";

import Holidays from "./pages/Holidays";
import Index from "./pages/Index";
import Hotels from "./pages/Hotels";
import Cabs from "./pages/Cabs";
import TirthYatra from "./pages/TirthYatra";
import YatraListing from "./pages/YatraListing";
import YatraDetails from "./pages/YatraDetails";
import MedicalTourism from "./pages/MedicalTourism";
import Vendor from "./pages/Vendor";
import HolidaysListing from "./pages/HolidaysListing";
import HolidayDetails from "./pages/HolidayDetails";
import HotelsListing from "./pages/HotelsListing";
import HotelDetails from "./pages/HotelDetails";
import VendorDetails from "./pages/VendorDetails";
import Login from "./pages/auth/Login";
import Registration from "./pages/auth/Registration";
import VerifyOtp from "./pages/auth/VerifyOtp";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import LoginOTP from "./pages/auth/LoginOTP";
import FreeTrial from "./pages/auth/FreeTrial";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Feedback from "./pages/Feedback";
import ListingHolidayPackages from "./admin-panel/pages/holiday_packges/ListingHolidayPackages";
import FormHolidayPackage from "./admin-panel/pages/holiday_packges/FormHolidayPackage";
import ListingYatras from "./admin-panel/pages/yatras/ListingYatras";
import FormYatra from "./admin-panel/pages/yatras/FormYatra";
import ListingHotels from "./admin-panel/pages/hotels/ListingHotels";
import FormHotel from "./admin-panel/pages/hotels/FormHotel";
import ListingCabs from "./admin-panel/pages/cabs/ListingCabs";
import FormCab from "./admin-panel/pages/cabs/FormCab";
import CabsListing from "./pages/CabsListing";
import ListingDoctors from "./admin-panel/pages/medical_tourism_doctor/ListingDoctors";
import FormDoctor from "./admin-panel/pages/medical_tourism_doctor/FormDoctor";
import FormHospital from "./admin-panel/pages/medical_tourism_hospital/FormHospital";
import ListingHospitals from "./admin-panel/pages/medical_tourism_hospital/ListingHospitals";
import SearchResultsPage from "./pages/SearchResultsPage";
import MedicalDetail from "./pages/MedicalDetail";
import Blogs from "./pages/Blogs";
import BlogDetail from "./pages/BlogDetail";
import LoginPopup from "./pages/popup/LoginPopup";
import ChangePassword from "./admin-panel/pages/ChangePassword";
import AutoLogin from "./pages/auth/AutoLogin";

import MySubscriptions from "./admin-panel/pages/subscriptions/MySubscriptions";
import MyLeads from "./admin-panel/pages/leads/MyLeads";
import ActivityLeads from "./admin-panel/pages/leads/ActivityLeads";
import VendorLeadDetail from "./admin-panel/pages/leads/LeadDetail";
import SuggestedCustomers from "./admin-panel/pages/recommendations/SuggestedCustomers";
import SuggestedVendors from "./pages/SuggestedVendors";
import LoadMoreRecommendations from "./pages/LoadMoreRecommendations";

// Super Admin Imports
import SuperAdminLogin from "./superadmin-panel/pages/auth/SuperAdminLogin";
import SuperAdminLayout from "./superadmin-panel/layout/SuperAdminLayout";
import SuperAdminDashboard from "./superadmin-panel/pages/Dashboard";
import VendorListing from "./superadmin-panel/pages/vendors/VendorListing";
import VendorDetail from "./superadmin-panel/pages/vendors/VendorDetail";
import VendorLeads from "./superadmin-panel/pages/vendors/VendorLeads";
import LeadListing from "./superadmin-panel/pages/leads/LeadListing";
import LeadDetail from "./superadmin-panel/pages/leads/LeadDetail";
import PlanListing from "./superadmin-panel/pages/plans/PlanListing";
import SubscriptionSettings from "./superadmin-panel/pages/settings/SubscriptionSettings";
import MetaTagsSettings from "./superadmin-panel/pages/settings/MetaTagsSettings";
import MetaTagsInjector from "./components/common/MetaTagsInjector";
import FrontendPageStatusGate from "./components/common/FrontendPageStatusGate";
import BlogListing from "./superadmin-panel/pages/blogs/BlogListing";
import FormBlog from "./superadmin-panel/pages/blogs/FormBlog";
import InformationPagesListing from "./superadmin-panel/pages/webpages/InformationPagesListing";
import FormInformationPage from "./superadmin-panel/pages/webpages/FormInformationPage";
import OtherPagesListing from "./superadmin-panel/pages/webpages/OtherPagesListing";
import FrontendPagesListing from "./superadmin-panel/pages/webpages/FrontendPagesListing";
import FrontendPageEditor from "./superadmin-panel/pages/webpages/FrontendPageEditor";
import InformationPageDetail from "./pages/InformationPageDetail";
import ListingApprovals from "./superadmin-panel/pages/listings/ListingApprovals";
import ListingApprovalDetail from "./superadmin-panel/pages/listings/ListingApprovalDetail";
import { SuperAdminPrivateRoute, VendorPrivateRoute } from "./context/ProtectedRoutes";
import Preloader from "./components/common/Preloader";
import SEO from "./components/common/SEO";

function App() {
  const location = useLocation();
  const [isAssetsLoading, setIsAssetsLoading] = useState(true);
  const currentArea = useRef(null); // 'admin', 'auth', or 'public'

  useEffect(() => {
    const adminPathPrefix = "/admin";
    const authRoutes = ["/login", "/registration", "/verify-otp", "/forgot-password", "/reset-password", "/login-otp"];

    const isAdminRoute = location.pathname.startsWith(adminPathPrefix);
    const isSuperAdminRoute = location.pathname.startsWith("/superadmin");
    const isAuthRoute = authRoutes.includes(location.pathname);

    let targetArea = 'public';
    if (isAdminRoute || isSuperAdminRoute) targetArea = 'admin';
    else if (isAuthRoute) targetArea = 'auth';

    // Only reload if we change major areas
    if (currentArea.current === targetArea) return;

    currentArea.current = targetArea;
    setIsAssetsLoading(true);

    const themeCssAsset = { id: "theme-css", href: "/assets/css/theme.css", tag: "link", parent: document.head };

    const adminAssets = [
      { id: "admin-favicon", rel: "shortcut icon", href: "/mangoloops-fevicon.png", tag: "link", parent: document.head, type: null },

      // CSS assets (added to head)
      { id: "admin-custom-theme", href: "/admin-assets/css/custom-theme.css", rel: "stylesheet", type: "text/css", tag: "link", parent: document.head },
      { id: "admin-grid-css", href: "/admin-assets/libs/gridjs/theme/mermaid.min.css", rel: "stylesheet", type: "text/css", tag: "link", parent: document.head },

      { id: "admin-bootstrap-css", href: "/admin-assets/css/bootstrap.min.css", rel: "stylesheet", type: "text/css", tag: "link", parent: document.head },
      { id: "admin-icons-css", href: "/admin-assets/css/icons.min.css", rel: "stylesheet", type: "text/css", tag: "link", parent: document.head },
      { id: "admin-app-css-head", href: "/admin-assets/css/app.min.css", rel: "stylesheet", type: "text/css", tag: "link", parent: document.head },
      { id: "admin-custom-css", href: "/admin-assets/css/custom.min.css", rel: "stylesheet", type: "text/css", tag: "link", parent: document.head },

      // JS assets (layout.js to head)
      { id: "admin-layout-js", src: "/admin-assets/js/layout.js", tag: "script", parent: document.head, dependsOn: [] },

      // Footer JS assets (added to body)
      { id: "admin-bootstrap-bundle-js", src: "/admin-assets/libs/bootstrap/js/bootstrap.bundle.min.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-simplebar-js", src: "/admin-assets/libs/simplebar/simplebar.min.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-waves-js", src: "/admin-assets/libs/node-waves/waves.min.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-feather-icons-js", src: "/admin-assets/libs/feather-icons/feather.min.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-lord-icon-js", src: "/admin-assets/js/pages/plugins/lord-icon-2.1.0.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-plugins-js", src: "/admin-assets/js/plugins.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-prism-js", src: "/admin-assets/libs/prismjs/prism.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-apexcharts-js", src: "/admin-assets/libs/apexcharts/apexcharts.min.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-dashboard-blog-init-js", src: "/admin-assets/js/pages/dashboard-blog.init.js", tag: "script", parent: document.body, dependsOn: ["admin-apexcharts-js"] },
      { id: "admin-grid-js", src: "/admin-assets/libs/gridjs/gridjs.umd.js", tag: "script", parent: document.body, dependsOn: [] },
      { id: "admin-grid-init-js", src: "/admin-assets/js/pages/gridjs.init.js", tag: "script", parent: document.body, dependsOn: [] },
      // { id: "admin-app-js-body", src: "/admin-assets/js/app.js", tag: "script", parent: document.body, dependsOn: [] },

    ];

    const loadScript = (asset) => {
      return new Promise((resolve, reject) => {
        if (document.getElementById(asset.id)) {
          console.log(`Script ${asset.id} already exists.`);
          return resolve();
        }
        const script = document.createElement("script");
        script.id = asset.id;
        script.src = asset.src;
        script.defer = asset.parent === document.body;
        script.onload = () => {
          console.log(`Loaded JS ${asset.id}`);
          resolve();
        };
        script.onerror = () => {
          console.error(`Failed to load JS ${asset.id}`);
          reject(new Error(`Failed to load script ${asset.src}`));
        };
        asset.parent.appendChild(script);
      });
    };

    const removeAllDynamicAssets = () => {
      const themeStylesheet = document.getElementById(themeCssAsset.id);
      if (themeStylesheet) {
        themeCssAsset.parent.removeChild(themeStylesheet);
        console.log(`Removed ${themeCssAsset.id}`);
      }

      adminAssets.forEach(asset => {
        const el = document.getElementById(asset.id);
        if (el) {
          asset.parent.removeChild(el);
          console.log(`Removed ${asset.id}`);
        }
      });
    };

    removeAllDynamicAssets();

    if (isAdminRoute || location.pathname.startsWith("/superadmin")) {
      adminAssets.filter(asset => asset.tag === "link").forEach(asset => {
        if (!document.getElementById(asset.id)) {
          const link = document.createElement("link");
          link.id = asset.id;
          link.rel = asset.rel || "stylesheet";
          if (asset.type) link.type = asset.type;
          link.href = asset.href;
          asset.parent.appendChild(link);
          console.log(`Added CSS ${asset.id}`);
        }
      });

      const loadAdminScripts = async () => {
        const jsAssetsToLoad = adminAssets.filter(asset => asset.tag === "script");
        const loadedScripts = new Set();

        const appJsAsset = jsAssetsToLoad.find(asset => asset.id === "admin-app-js-body");
        const otherJsAssets = jsAssetsToLoad.filter(asset => asset.id !== "admin-app-js-body");

        for (const asset of otherJsAssets) {
          if (asset.dependsOn && asset.dependsOn.length > 0) {
            const dependenciesNotLoaded = asset.dependsOn.filter(depId => !loadedScripts.has(depId));
            for (const depId of dependenciesNotLoaded) {
              const depAsset = adminAssets.find(a => a.id === depId);
              if (depAsset && !document.getElementById(depAsset.id)) {
                await loadScript(depAsset);
                loadedScripts.add(depAsset.id);
              }
            }
          }
          if (!document.getElementById(asset.id)) {
            await loadScript(asset);
            loadedScripts.add(asset.id);
          }
        }

        if (appJsAsset) {
          let retries = 0;
          const maxRetries = 5;
          const retryDelay = 200;

          const tryLoadAppJs = async () => {
            if (document.getElementById(appJsAsset.id)) {
              console.log('app.js already loaded, attempting to re-execute logic.');

              return;
            }

            try {
              await loadScript(appJsAsset);
              loadedScripts.add(appJsAsset.id);
              console.log('app.js loaded successfully. Checking for errors...');

              await new Promise(r => setTimeout(r, 100));

              console.log('app.js seems to be ready.');
            } catch (error) {
              console.error(`Error loading app.js: ${error.message}`);
              retries++;
              if (retries < maxRetries) {
                console.log(`Retrying app.js load in ${retryDelay}ms (Attempt ${retries}/${maxRetries})...`);
                setTimeout(tryLoadAppJs, retryDelay);
              } else {
                console.error(`Failed to load app.js after ${maxRetries} attempts.`);
              }
            }
          };
          tryLoadAppJs();
        }
      };

      loadAdminScripts().finally(() => {
        setTimeout(() => setIsAssetsLoading(false), 500);
      });

    } else if (!isAuthRoute) {
      if (!document.getElementById(themeCssAsset.id)) {
        const link = document.createElement("link");
        link.id = themeCssAsset.id;
        link.rel = "stylesheet";
        link.href = themeCssAsset.href;
        link.onload = () => {
          setIsAssetsLoading(false);
        };
        link.onerror = () => {
          setIsAssetsLoading(false);
        };
        themeCssAsset.parent.appendChild(link);
        console.log(`Added ${themeCssAsset.id}`);
      } else {
        setIsAssetsLoading(false);
      }
    } else {
      setIsAssetsLoading(false);
    }

    return () => {
    };

  }, [location.pathname]);

  return (
    <>
      <SEO />
      <MetaTagsInjector />
      <ToastContainer />
      <LoginPopup autoShow={true} />
      {isAssetsLoading && <Preloader />}
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/holidays" element={<Holidays />} />
        <Route path="/hotels" element={<Hotels />} />
        <Route path="/cabs" element={<Cabs />} />
        <Route path="/tirthyatra" element={<TirthYatra />} />
        <Route path="/yatra-list" element={<YatraListing />} />
        <Route path="/yatra-details/:slug" element={<YatraDetails />} />
        <Route path="/medicaltourism" element={<MedicalTourism />} />
        <Route path="/vendor" element={<Vendor />} />
        <Route path="/holidays-list" element={<HolidaysListing />} />
        <Route path="/holidays_list" element={<HolidaysListing />} />
        <Route path="/holiday-details/:slug" element={<HolidayDetails />} />
        <Route path="/hotel-list" element={<HotelsListing />} />
        <Route path="/hotel-details/:slug" element={<HotelDetails />} />
        <Route path="/vendor-details/:company_slug/:encoded_id" element={<VendorDetails />} />
        <Route path="/vendor-details/:encoded_id" element={<VendorDetails />} />
        <Route path="/vendor_details/:encoded_id" element={<VendorDetails />} />
        <Route path="/vendor_details" element={<VendorDetails />} />
        <Route path="/login" element={<Login />} />
        <Route path="/registration" element={<Registration />} />
        <Route path="/free-trial" element={<FreeTrial />} />
        <Route path="/verify-otp" element={<VerifyOtp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/login-otp" element={<LoginOTP />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/cabs/search" element={<CabsListing />} />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/medical-details/:type/:id" element={<MedicalDetail />} />
        <Route path="/admin/auto-login" element={<AutoLogin />} />
        <Route path="/medical-details/:type/:slug" element={<MedicalDetail />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/blog/:slug" element={<BlogDetail />} />
        <Route path="/page/:slug" element={<InformationPageDetail />} />
        <Route path="/suggested-vendors" element={<SuggestedVendors />} />
        <Route path="/vendor-recommendations/load-more/:token" element={<LoadMoreRecommendations />} />

        {/* admin routes */}
        <Route
          path="/admin" element={
            <VendorPrivateRoute>
              <Provider store={adminStore}>
                <AdminLayout />
              </Provider>
            </VendorPrivateRoute>
          }>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route index element={<AdminDashboard />} />
          <Route path="kyc-verification" element={<KycVerification />} />
            <Route path="profile-setup" element={<ProfileSetup />} />
            <Route path="kyc" element={<KYC />} />
          <Route path="holiday-packages" element={<ListingHolidayPackages />} />
          <Route path="add-holiday-packages" element={<FormHolidayPackage />} />
          <Route path="hotels" element={<ListingHotels />} />
          <Route path="add-hotel" element={<FormHotel />} />
          <Route path="edit-hotel/:id" element={<FormHotel />} />
          <Route path="edit-holiday-package/:id" element={<FormHolidayPackage />} />
          <Route path="yatras" element={<ListingYatras />} />
          <Route path="add-yatra" element={<FormYatra />} />
          <Route path="edit-yatra/:id" element={<FormYatra />} />
          <Route path="cabs" element={<ListingCabs />} />
          <Route path="add-cab" element={<FormCab />} />
          <Route path="edit-cab/:id" element={<FormCab />} />
          <Route path="medical-tourism-doctors" element={<ListingDoctors />} />
          <Route path="add-doctor" element={<FormDoctor />} />
          <Route path="edit-doctor/:id" element={<FormDoctor />} />
          <Route path="medical-tourism-hospitals" element={<ListingHospitals />} />
          <Route path="add-hospital" element={<FormHospital />} />
          <Route path="edit-hospital/:id" element={<FormHospital />} />
          <Route path="subscriptions" element={<MySubscriptions />} />
          <Route path="my-leads" element={<MyLeads />} />
          <Route path="activity-leads" element={<ActivityLeads />} />
          <Route path="my-leads/:id" element={<VendorLeadDetail />} />
          <Route path="suggested-customers" element={<SuggestedCustomers />} />
          <Route path="change-password" element={<ChangePassword />} />
          <Route path="digilocker-callback" element={<DigiLockerCallback />} />
        </Route>

        {/* Super Admin Routes */}
        <Route path="/superadmin/login" element={<SuperAdminLogin />} />
        <Route
          path="/superadmin"
          element={
            <SuperAdminPrivateRoute>
              <SuperAdminLayout />
            </SuperAdminPrivateRoute>
          }
        >
          <Route path="dashboard" element={<SuperAdminDashboard />} />
          <Route index element={<SuperAdminDashboard />} />
          <Route path="vendors/detail/:id" element={<VendorDetail />} />
          <Route path="vendors/detail/:id/leads" element={<VendorLeads />} />
          <Route path="vendors/:type" element={<VendorListing />} />
          <Route path="vendors" element={<VendorListing />} />
          <Route path="listings-approval/:module/:id" element={<ListingApprovalDetail />} />
          <Route path="listings-approval/:module" element={<ListingApprovals />} />
          <Route path="listings-approval" element={<Navigate to="/superadmin/listings-approval/holidays" replace />} />
          <Route path="leads" element={<LeadListing />} />
          <Route path="leads/detail/:id" element={<LeadDetail />} />
          <Route path="plans" element={<PlanListing />} />
          <Route path="settings/subscription" element={<SubscriptionSettings />} />
          <Route path="settings/meta-tags" element={<MetaTagsSettings />} />
          <Route path="blogs" element={<BlogListing />} />
          <Route path="blogs/add" element={<FormBlog />} />
          <Route path="blogs/edit/:id" element={<FormBlog />} />
          <Route path="web-pages/information" element={<InformationPagesListing />} />
          <Route path="web-pages/information/add" element={<FormInformationPage />} />
          <Route path="web-pages/information/edit/:id" element={<FormInformationPage />} />
          <Route path="web-pages/other" element={<OtherPagesListing />} />
          <Route path="web-pages/header-pages" element={<FrontendPagesListing />} />
          <Route path="web-pages/header-pages/:pageKey" element={<FrontendPageEditor />} />
        </Route>

      </Routes>
    </>
  );
}

export default App;
